<template>
  <section
    v-if="fields && items.length"
    :class="rootClass"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-news-section__grid">
        <article
          v-for="(item, index) in items"
          :key="item.id"
          class="c-news-section__card"
          :class="cardClass(index)"
        >
          <div v-if="item.fields?.image" class="c-news-section__media">
            <AdaptiveImage
              class="c-news-section__image"
              :field="getAdaptiveImageField(item.fields.image)"
              :is-background="false"
              :show="true"
              :enable-scroll-detection="false"
              :show-image-redius="false"
            />
          </div>

          <div class="c-news-section__content">
            <div class="c-news-section__meta">
              <span
                v-if="item.fields?.tag?.value"
                class="c-news-section__tag"
                :class="getTagClass(item.fields.tag.value)"
              >
                {{ item.fields.tag.value }}
              </span>
              <time v-if="item.fields?.date?.value" class="c-news-section__date">
                {{ item.fields.date.value }}
              </time>
            </div>

            <RichText
              v-if="item.fields?.title?.value"
              class="c-news-section__title"
              :field="item.fields.title"
              tag="h3"
            />

            <RichText
              v-if="index === 0 && item.fields?.summary?.value"
              class="c-news-section__summary"
              :field="item.fields.summary"
            />
          </div>

          <a
            v-if="getLinkHref(item)"
            class="c-news-section__link"
            :href="getLinkHref(item)"
            :target="item.fields?.link?.value?.target"
            :rel="item.fields?.link?.value?.target === '_blank' ? 'noopener noreferrer' : undefined"
            :aria-label="getLinkLabel(item, index)"
          >
            <span aria-hidden="true"></span>
          </a>
        </article>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type { NewsSectionItemFields, NewsSectionProps } from '@/types/components/NewsSection';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

const props = defineProps<NewsSectionProps>();
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-news-section'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawItems = computed<ScItem<NewsSectionItemFields>[]>(() => {
  const newsItems = utils.sc.getFieldObj(fields.value, 'items');
  return Array.isArray(newsItems) ? newsItems : [];
});

const getAdaptiveImageField = (image: any) => {
  if (image?.fields?.desktopImage) return image;
  if (!image?.value?.src) return null;

  return {
    fields: {
      desktopImage: image,
      mobileImage: image,
      tabletImage: image,
    },
  };
};

const items = computed(() => rawItems.value.filter((item) => item.fields));

const getTagClass = (tag: string) =>
  tag.toLowerCase() === 'news' ? 'c-news-section__tag--news' : 'c-news-section__tag--article';

const getLinkHref = (item: ScItem<NewsSectionItemFields>) => item.fields?.link?.value?.href || '';

const getLinkLabel = (item: ScItem<NewsSectionItemFields>, index: number) => {
  const linkValue = item.fields?.link?.value || {};
  return linkValue.text || linkValue.title || item.fields?.title?.value || `News item ${index + 1}`;
};

const cardClass = (index: number) => {
  if (index === 0) return 'c-news-section__card--feature';
  if (index === 3) return 'c-news-section__card--wide';
  return 'c-news-section__card--compact';
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-news-section {
  padding: 40px 0;
  background: #f8f8f9;

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    max-width: 1440px;
    margin: 0 auto;
  }

  &__card {
    position: relative;
    min-width: 0;
    overflow: hidden;
    border-radius: 8px;
    background: $white;
    padding: 24px;
  }

  &__card--feature {
    display: flex;
    flex-direction: column;

    .c-news-section__media {
      order: 3;
      margin-top: 40px;
    }

    .c-news-section__content {
      display: contents;
    }

    .c-news-section__meta {
      order: 4;
      margin-top: 26px;
    }

    .c-news-section__title {
      order: 1;
      display: -webkit-box;
      overflow: hidden;
      padding-right: 52px;
      font-size: 14px;
      line-height: 1.22;
      -webkit-box-orient: vertical;
      @include line-clamp(2);
    }

    .c-news-section__summary {
      order: 2;
      margin-top: 20px;
    }
  }

  &__card--compact {
    display: grid;
    grid-template-columns: 102px minmax(0, 1fr);
    column-gap: 16px;
    align-items: center;

    .c-news-section__media {
      grid-row: 1;
    }

    .c-news-section__content {
      min-width: 0;
    }

    .c-news-section__meta {
      margin-bottom: 8px;
    }

    .c-news-section__title {
      display: -webkit-box;
      overflow: hidden;
      font-size: 14px;
      line-height: 1.26;
      -webkit-box-orient: vertical;
      @include line-clamp(2);
    }
  }

  &__card--wide {
    display: flex;
    flex-direction: column;

    .c-news-section__content {
      display: contents;
    }

    .c-news-section__meta {
      order: 1;
    }

    .c-news-section__media {
      order: 2;
      margin-top: 10px;
    }

    .c-news-section__title {
      order: 3;
      display: -webkit-box;
      overflow: hidden;
      margin-top: 10px;
      padding-right: 52px;
      font-size: 14px;
      line-height: 1.22;
      -webkit-box-orient: vertical;
      @include line-clamp(2);
    }
  }

  &__media {
    overflow: hidden;
    border-radius: 4px;
    background: #e8ece9;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;

    > div,
    .e-background-image,
    .e-background-image__img,
    img {
      width: 100%;
      height: 100%;
    }

    img {
      display: block;
      object-fit: cover;
    }
  }

  &__content {
    min-width: 0;
  }

  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 2px;
    color: $white;
    background: #8fa68f;
    font-size: 12px;
    line-height: 1;
    font-weight: 500;
  }

  &__tag--news {
    color: #000;
    background: #dbc9ac;
  }

  &__tag--article {
    color: $white;
    background: #8fa690;
  }

  &__date {
    flex: 0 0 auto;
    color: #71788b;
    font-size: 14px;
    line-height: 1.2;
  }

  &__title {
    margin: 0;
    color: #3d3d3d;
    font-weight: 700;
    letter-spacing: 0;

    :deep(p) {
      margin: 0;
    }
  }

  &__summary {
    display: -webkit-box;
    overflow: hidden;
    margin-top: 20px;
    color: #4f596d;
    font-size: 14px;
    line-height: 1.6;
    -webkit-box-orient: vertical;
    @include line-clamp(2);

    p {
      margin: 0;
    }
  }

  &__link {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: block;
    color: #739b78;
    text-decoration: none;

    &:focus-visible {
      outline: 2px solid #739b78;
      outline-offset: -6px;
    }

    span {
      position: absolute;
      right: 28px;
      bottom: 24px;
      display: none;
      width: 49px;
      height: 49px;
      border-radius: 50%;
      background: #f8f8f8;

      &::before,
      &::after {
        content: '';
        position: absolute;
        box-sizing: border-box;
      }

      &::before {
        left: 15px;
        top: 23px;
        width: 24px;
        height: 3px;
        background: currentColor;
        transform: rotate(-45deg);
      }

      &::after {
        right: 12px;
        top: 15px;
        width: 14px;
        height: 14px;
        border-top: 3px solid currentColor;
        border-right: 3px solid currentColor;
      }
    }
  }

  @include tablet {
    &__grid {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
    }

    &__card--feature {
      grid-row: span 3;

      .c-news-section__title {
        font-size: 18px;
      }
    }

    &__card--compact {
      grid-template-columns: 116px minmax(0, 1fr) 36px;

      .c-news-section__title {
        font-size: 18px;
      }
    }

    &__card--wide {
      .c-news-section__title {
        font-size: 18px;
      }
    }

    &__summary {
      font-size: 14px;
    }

    &__link {
      display: block;

      span {
        display: block;
      }
    }

    &__card--feature {
      .c-news-section__link span {
        top: 30px;
        bottom: auto;
        display: block;
      }
    }
  }

  @include tablet-landscape {
    &__grid {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
    }

    &__card--feature {
      .c-news-section__title {
        font-size: 18px;
      }
    }

    &__card--compact {
      .c-news-section__title {
        font-size: 18px;
      }
    }
  }
}
</style>
